import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { CustomerAuth } from '../../models/CustomerAuth';
import { customerBody, paymentBody } from '../../models/Converter';
import Order from '../../models/Order';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import { AuthLoginResponse } from '../../models/AuthLoginResponse';
import MyOrdersResponse from '../../models/MyOrdersResponse';
import { sanitizeId } from '../../utils/utils';
import Payment from '../../models/Payment';
import dayjs from 'dayjs';
import Table from '../../models/Table';
import { NominatimPlace } from '../../models/NominatimPlace';
import Customer from '../../models/Customer';
import { AuthStrategy } from '../../models/AuthStrategy';
import PaySession from '../../models/PaySession';
import { AuthenticationService } from '../authentication/authentication.service';
import Venue from '../../models/Venue';
import PaySessionRequest from '../../models/PaySessionRequest';
import { ReserveRequest } from '../../models/ReserveRequest';
import { PaySessionSplitType } from '../../enums/PaySessionSplitType';
import PiggyGiftCard from '../../models/PiggyGiftCard';
import { AdyenPaymentMethodResponse } from '../../models/AdyenPaymentMethodResponse';
import { ChecksResponse } from '../../models/ChecksResponse';
import { UiComponent } from '../../models/UiComponent';
import PromoCode from '../../models/PromoCode';
import { LoyaltyResponse } from '../../models/LoyaltyResponse';
import PiggyReward from '../../models/PiggyReward';
import PiggyContact from '../../models/PiggyContact';
import { CNCDiscount, SmoothrCNCDiscount } from '../../models/CNCDiscount';
import { SimphonyDiscountingTableResult } from '../../models/SimphonyDiscountingTableResult';

const ORDER_SERVICE = '/v2/order/';
const CUSTOMER_SERVICE = '/v1/customer/';
const GENERAL_SERVICE = '/v1/general/';
const PAYMENT_SERVICE = '/v3/payment/';
const SIMPHONY_SERVICE_V2 = '/v2/simphony/';
const AUTH_SERVICE = '/v1/auth/';
const WALLET_SERVICE = '/v1/wallet';
const PAY_SESSION_SERVICE = '/v1/paysession/';
const SOCKET_IO_SERVICE = '/v1/socketio/';
const PIGGY_SERVICE = '/v1/piggy';
const UI_SERVICE = '/v1/ui/';

const VENUE = 'venue/';
const LOGIN = 'login/';

const LOGIN_ENDPOINT = AUTH_SERVICE + LOGIN;
const CUSTOMER_ENDPOINT = CUSTOMER_SERVICE;
const VENUE_ENDPOINT = GENERAL_SERVICE + VENUE;

const ORDER_ENDPOINT = ORDER_SERVICE;
const CUSTOMER_LOGIN_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/login';
const CUSTOMER_LOGOUT_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/logout';
const CUSTOMER_REGISTER_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/register';
const CUSTOMER_VERIFY_EMAIL_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/verify/email';
const CUSTOMER_SEND_EMAIL_VERIFICATION_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/verify/resend';
const CUSTOMER_ONE_TIME_LOGIN_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/token/code';
const CUSTOMER_FORGOT_PASSWORD_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/password/forgot/request';
const CUSTOMER_RESET_PASSWORD_ENDPOINT = CUSTOMER_ENDPOINT + 'auth/password/forgot/reset';
const CUSTOMER_DELETE_ACCOUNT_ENDPOINT = CUSTOMER_ENDPOINT;
const MAINTENANCE_ENDPOINT = GENERAL_SERVICE + 'maintenance/status';
const CANCEL_COMMAND = '/cancel/customer';
const KILL_CHECK = `${PAY_SESSION_SERVICE}kill`;
const EXTEND_SOCKET_TIMEOUT = `${PAY_SESSION_SERVICE}socket-timeout`;

/**
 * Marks a request that should be authorized with the anonymous customer token
 * (managed by CustomerAuthService) instead of the paySession token.
 */
export const USE_CUSTOMER_TOKEN = new HttpContextToken<boolean>(() => false);

export const baseUrlInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
	// Only prefix relative URLs
	if (/^https?:\/\//i.test(req.url)) {
		return next(req);
	}
	const apiReq = req.clone({ url: `${environment.baseUrl}${req.url}` });
	return next(apiReq);
};
export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
	// Requests flagged for the anonymous customer token are handled by customerAuthInterceptor.
	if (req.context.get(USE_CUSTOMER_TOKEN)) {
		return next(req);
	}

	const auth = inject(AuthenticationService);
	const token = auth.token;

	if (!token) {
		return next(req);
	}

	const authReq = req.clone({
		setHeaders: { authorization: `Bearer ${token}` },
	});

	return next(authReq);
};

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	private httpClient: HttpClient = inject(HttpClient);
	private authenticationService = inject(AuthenticationService);

	constructor() {}

	public authLogin(authLogin: { email: string; password: string }): Observable<AuthLoginResponse> {
		return this.httpClient.post<AuthLoginResponse>(LOGIN_ENDPOINT, authLogin);
	}

	public getCustomerOrders(userUid: string): Observable<MyOrdersResponse> {
		return this.httpClient.get<MyOrdersResponse>(PAYMENT_SERVICE + 'customer/' + sanitizeId(userUid));
	}

	public createPayment(payment: Partial<Payment>): Observable<Payment> {
		// return this.createPaymentMultiple([payment]).pipe(map(it => it[0]));
		return this.httpClient.post<Payment>(PAYMENT_SERVICE, paymentBody(payment, true));
	}

	public createPaymentMultiple(payments: Partial<Payment>[]): Observable<Payment[]> {
		return this.httpClient.post<Payment[]>(
			PAYMENT_SERVICE + 'multiple',
			payments.map(payment => paymentBody(payment, true))
		);
	}

	public finishPaypalPayment(paypalOrderId: string): Observable<Payment> {
		return this.httpClient.patch<Payment>(PAYMENT_SERVICE + paypalOrderId + '/paypal/pay', null);
	}

	public getOrder(order: string): Observable<Order> {
		return this.httpClient.get<Order>(ORDER_ENDPOINT + order);
	}

	// Customer

	public getCustomer(): Observable<Customer> {
		return this.httpClient.get<Customer>(CUSTOMER_ENDPOINT);
	}

	public patchCustomer(customer: Customer): Observable<Customer> {
		return this.httpClient.patch<Customer>(CUSTOMER_ENDPOINT, customerBody(customer));
	}

	public signIn(strategy: AuthStrategy): Observable<{ url: string; isRedirect: boolean }> {
		return this.httpClient.post<{ url: string; isRedirect: boolean }>(CUSTOMER_LOGIN_ENDPOINT, {
			strategy,
			customerGroup: environment.customerGroup,
		});
	}

	public signInAnonymous(uid?: string): Observable<CustomerAuth> {
		return this.httpClient.post<CustomerAuth>(CUSTOMER_LOGIN_ENDPOINT, {
			strategy: AuthStrategy.ANONYMOUS,
			uid: uid ? uid : undefined,
			customerGroup: environment.customerGroup,
		});
	}

	public signInWithCredentials(email: string, password: string): Observable<CustomerAuth> {
		return this.httpClient.post<CustomerAuth>(CUSTOMER_LOGIN_ENDPOINT, {
			strategy: AuthStrategy.CREDENTIALS,
			customerGroup: environment.customerGroup,
			email,
			password,
		});
	}

	public signUpWithCredentials(data?: Partial<Customer>, password?: string): Observable<Customer> {
		return this.httpClient.post<Customer>(CUSTOMER_REGISTER_ENDPOINT, {
			...data,
			strategy: AuthStrategy.CREDENTIALS,
			customerGroup: environment.customerGroup,
			preferredVenues: data?.preferredVenues ?? [],
			password,
		});
	}

	public verifyEmail(token: string): Observable<CustomerAuth> {
		return this.httpClient.post<CustomerAuth>(CUSTOMER_VERIFY_EMAIL_ENDPOINT, {
			token,
		});
	}

	public forgotPassword(email: string): Observable<void> {
		return this.httpClient.post<void>(CUSTOMER_FORGOT_PASSWORD_ENDPOINT, {
			email,
			customerGroup: environment.customerGroup,
		});
	}

	public oneTimeLogin(id: string, authCode: string): Observable<CustomerAuth> {
		return this.httpClient.post<CustomerAuth>(CUSTOMER_ONE_TIME_LOGIN_ENDPOINT, {
			id,
			authCode,
		});
	}

	public logout(): Observable<void> {
		return this.httpClient.post<void>(CUSTOMER_LOGOUT_ENDPOINT, null);
	}

	public resetPassword(token: string, password: string): Observable<void> {
		return this.httpClient.post<void>(CUSTOMER_RESET_PASSWORD_ENDPOINT, {
			token,
			password,
		});
	}

	public sendEmailVerification(email: string): Observable<Object> {
		return this.httpClient.post(CUSTOMER_SEND_EMAIL_VERIFICATION_ENDPOINT, {
			email,
			customerGroup: environment.customerGroup,
		});
	}

	public deleteAccount(): Observable<Object> {
		return this.httpClient.delete(CUSTOMER_DELETE_ACCOUNT_ENDPOINT);
	}

	public isMaintenanceActive(): Observable<boolean> {
		return this.httpClient.get<boolean>(MAINTENANCE_ENDPOINT);
	}

	public getPayment(payment: string): Observable<Payment> {
		return this.httpClient.get<Payment>(PAYMENT_SERVICE + 'byId/' + payment);
	}

	public getAdyenMethods(venue: string): Observable<AdyenPaymentMethodResponse> {
		return this.httpClient.get<AdyenPaymentMethodResponse>(PAYMENT_SERVICE + 'adyen/venues/' + venue + '/methods');
	}

	public cancelOrder(order: string): Observable<void> {
		return this.httpClient.patch<void>(ORDER_ENDPOINT + order + CANCEL_COMMAND, null);
	}

	public mapSearch(query: string, limit: number): Observable<NominatimPlace[]> {
		const countryCodes = environment.countryList.reduce((previousValue, currentValue) => previousValue + ',' + currentValue);
		return this.httpClient.get<NominatimPlace[]>(
			`/v1/maps/search?limit=${limit}&format=jsonv2&addressdetails=1&q=${query}&countrycodes=${countryCodes}`
		);
	}

	public geocode(lat: number, lng: number): Observable<NominatimPlace> {
		return this.httpClient.get<NominatimPlace>(`/v1/maps/reverse?limit=5&format=jsonv2&addressdetails=1&lat=${lat}&lon=${lng}`);
	}

	public getLazyVenue(venueId: string, isIncludeCustomerGroup: boolean = false): Observable<Venue> {
		return this.httpClient.get<Venue>(VENUE_ENDPOINT + sanitizeId(venueId, isIncludeCustomerGroup));
	}

	public getTable(venue: string, tableNumber: string): Observable<Table> {
		return this.httpClient.get<Table>(VENUE_ENDPOINT + venue + '/table/' + tableNumber);
	}

	public getOrdersByTable(venue: string, table: string) {
		const yesterday = dayjs().add(-1, 'days').toISOString();
		return this.httpClient.get(`${ORDER_ENDPOINT}filtered?tables[]=${table}&venue[]=${venue}&from=${yesterday}`);
	}

	public invalidateOrder(orderId: string): Observable<void> {
		return this.httpClient.patch<void>(ORDER_ENDPOINT + orderId + '/failed', null);
	}

	public updateOrder(order: Order): Observable<void> {
		return this.httpClient.patch<void>(ORDER_ENDPOINT + order._id, order);
	}

	public applePaySession(
		venueId: string,
		body: {
			url: string;
			venue: string;
			channel: string;
			customerGroup: string;
		}
	) {
		return this.httpClient.post(PAYMENT_SERVICE + 'apple/session', body);
	}

	public getPaySession(): Observable<PaySession> {
		return this.httpClient.get<PaySession>(PAY_SESSION_SERVICE);
	}

	public getPaySessionByVenueAndTable(venue: string, table: string) {
		return this.httpClient.get<PaySession>(PAY_SESSION_SERVICE + '/' + venue + '/' + table);
	}

	public resume(): Observable<PaySession> {
		return this.httpClient.get<PaySession>(PAY_SESSION_SERVICE + 'resume');
	}

	public leave(): Observable<PaySession> {
		return this.httpClient.get<PaySession>(PAY_SESSION_SERVICE + 'leave');
	}

	public participate(participationRequest: PaySessionRequest): Observable<PaySession> {
		return this.httpClient.post<PaySession>(PAY_SESSION_SERVICE, participationRequest);
	}

	public getChecks(): Observable<Order[]> {
		return this.httpClient.get<Order[]>(PAY_SESSION_SERVICE + 'checks');
	}

	public reserve(ids: string[]): Observable<Order[]> {
		const body: ReserveRequest = {
			items: ids.map(it => ({ id: it })),
			ifVersion: +(this.authenticationService.paySession?.serverVersion ?? ''),
			opId: '1',
		};
		return this.httpClient.post<Order[]>(PAY_SESSION_SERVICE + 'reserve', body);
	}

	public selectSplit(splitType: PaySessionSplitType | null, guestCount: number): Observable<PaySession> {
		return this.httpClient.patch<PaySession>(PAY_SESSION_SERVICE, {
			splitType,
			guestCount,
		});
	}

	public generateOrderForParticipant(): Observable<Order> {
		return this.httpClient.post<Order>(PAY_SESSION_SERVICE + 'orders', {
			serverVersion: +(this.authenticationService.paySession?.serverVersion ?? ''),
		});
	}

	public generateOrderAllForParticipant(): Observable<Order> {
		return this.httpClient.post<Order>(PAY_SESSION_SERVICE + 'orders', {
			reserveAllOpenItems: true,
			serverVersion: +(this.authenticationService.paySession?.serverVersion ?? ''),
		});
	}

	public discounting(promoCodes: string[], contactUuid?: string): Observable<SimphonyDiscountingTableResult[]> {
		return this.httpClient.post<SimphonyDiscountingTableResult[]>(PAY_SESSION_SERVICE + 'discounting', {
			promoCodes,
			ifVersion: +(this.authenticationService.paySession?.serverVersion ?? ''),
			...(contactUuid ? { contactUuid } : {}),
		});
	}

	public getChecksOfTable(venue: string, table: string): Observable<ChecksResponse> {
		return this.httpClient.get<ChecksResponse>(PAY_SESSION_SERVICE + 'checks/' + venue + '/' + table);
	}

	public getGiftCardByCode(code: string, venueId: string): Observable<PiggyGiftCard> {
		return this.httpClient.get<PiggyGiftCard>(`${PIGGY_SERVICE}/gift-card/balance?hash=${code}&venue=${venueId}`);
	}

	public getPromoCode(body: { code: string; venue: string }): Observable<PromoCode> {
		return this.httpClient.post<PromoCode>(GENERAL_SERVICE + 'promoCode/code', body);
	}

	/**
	 * Creates (or updates) promo codes on the fly from the Piggy vouchers matching
	 * the given code and returns the resulting promo code ids.
	 */
	public createPromoCodeOnTheFly(body: { venue: string; code: string }): Observable<PromoCode> {
		return this.httpClient.post<PromoCode>(`${PIGGY_SERVICE}/vouchers`, body, {
			context: new HttpContext().set(USE_CUSTOMER_TOKEN, true),
		});
	}

	public getMembership(venue: string, code: string): Observable<LoyaltyResponse> {
		return this.httpClient.get<LoyaltyResponse>(
			`${PIGGY_SERVICE}/contact/identifier?venue=${venue}&identifier=${encodeURIComponent(code)}`,
			{
				context: new HttpContext().set(USE_CUSTOMER_TOKEN, true),
			}
		);
	}

	/**
	 * Returns a contact's prepaid and credit balances, looked up by identifier (e.g. email).
	 * Unlike getMembership (/contact), this route only requires authentication.
	 */
	public getContactByIdentifier(venue: string, identifier: string): Observable<PiggyContact> {
		return this.httpClient.get<PiggyContact>(
			`${PIGGY_SERVICE}/contact/identifier?venue=${venue}&identifier=${encodeURIComponent(identifier)}`,
			{ context: new HttpContext().set(USE_CUSTOMER_TOKEN, true) }
		);
	}

	public getRewards(venue: string, contactUuid: string): Observable<PiggyReward[]> {
		return this.httpClient.get<PiggyReward[]>(
			`${PIGGY_SERVICE}/rewards?customerGroup=${environment.customerGroup}&venue=${venue}&contact_uuid=${contactUuid}`,
			{ context: new HttpContext().set(USE_CUSTOMER_TOKEN, true) }
		);
	}

	public getPromoCodesByPiggyRewardUuids(rewardUuids: string[]): Observable<PromoCode[]> {
		const params = rewardUuids.map(uuid => `piggyRewardUuid[]=${encodeURIComponent(uuid)}`).join('&');
		return this.httpClient
			.get<{ data: PromoCode[] }>(`${GENERAL_SERVICE}promoCode?${params}`, {
				context: new HttpContext().set(USE_CUSTOMER_TOKEN, true),
			})
			.pipe(map(response => this.filterCurrentlyAvailablePromoCodes(response.data)));
	}

	public getCNCDiscountsToday(venue: string): Observable<SmoothrCNCDiscount[]> {
		return this.httpClient.get<SmoothrCNCDiscount[]>(`${SIMPHONY_SERVICE_V2}cnc/${venue}/discounts-today`);
	}

	/**
	 * Fetches the CNC data for a single discount by its objectNum.
	 * Returns null when there is no CNC data for the discount (the EP does not fail in that case).
	 */
	public getCNCDiscount(venue: string, objectNum: number | string): Observable<CNCDiscount | null> {
		return this.httpClient.get<CNCDiscount | null>(`${SIMPHONY_SERVICE_V2}cnc/${venue}/discounts/${objectNum}`);
	}

	public sendPaymentEmail(payment: string, email: string): Observable<void> {
		return this.httpClient.post<void>(`${PAYMENT_SERVICE}export/checkout/email/${payment}?email=${email}&v2=true`, { email });
	}

	public killCheck(readableId: string, tableNumber: string) {
		return this.httpClient.delete(`${KILL_CHECK}/${readableId}/${tableNumber}`);
	}

	public setSocketTimeout(timeoutInMs: number) {
		return this.httpClient.patch<void>(EXTEND_SOCKET_TIMEOUT, { timeout: timeoutInMs });
	}

	public checkChecksAvailable(venue: string, tableNumber: string, checkNumbers: string[]) {
		return this.httpClient.post<boolean>(`${SIMPHONY_SERVICE_V2}checks/${venue}/${tableNumber}/availability`, checkNumbers);
	}

	public getBanners(): Observable<UiComponent[]> {
		return this.httpClient.get<any>(`${UI_SERVICE}banners/?page=1&limit=30&customerGroup=cinemaxx`).pipe(map(it => it.data));
	}

	public getSliders(): Observable<UiComponent[]> {
		return this.httpClient
			.get<any>(`${UI_SERVICE}sliders/?page=1&limit=30&customerGroup=${environment.customerGroup}`)
			.pipe(map(it => it.data));
	}

	public getCards(): Observable<UiComponent[]> {
		return this.httpClient
			.get<any>(`${UI_SERVICE}cards/?page=1&limit=30&customerGroup=${environment.customerGroup}`)
			.pipe(map(it => it.data));
	}

	private filterCurrentlyAvailablePromoCodes(promoCodes: PromoCode[]): PromoCode[] {
		const today = new Date();
		const currentDay = today.getDay(); // Sunday: 0, Monday: 1, ...
		return promoCodes.filter(promo => {
			if (!promo.active) {
				return false;
			}

			const fromDate = promo.from ? new Date(promo.from) : null;
			const toDate = promo.to ? new Date(promo.to) : null;

			if (fromDate && today < fromDate) {
				return false;
			}
			if (toDate && today > toDate) {
				return false;
			}

			const byWeekday = (promo.availability as any)?.byWeekday;
			return !(byWeekday && byWeekday[currentDay] === false);
		});
	}
}

export { PAYMENT_SERVICE, WALLET_SERVICE, PAY_SESSION_SERVICE, SOCKET_IO_SERVICE };
